import { ArrowUpRight } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const SERVICES = [
  {
    id: "stat-lab",
    title: "STAT Lab Specimens",
    description: "Urgent, time-critical pickup and expedited direct delivery of human diagnostic specimens with barcode-scanned custody at every handoff.",
    badge: "Urgent STAT Priority",
  },
  {
    id: "pharmacy",
    title: "Pharmacy Delivery",
    description: "On-demand and scheduled routes for customized compounds, specialty medications, and hospital-to-patient treatments — fast, reliable same-day home delivery.",
    badge: "Licensed & Verified",
  },
  {
    id: "equipment",
    title: "Equipment Transport",
    description: "Secure handling for delicate diagnostic devices, surgical tooling trays, and specialized biomedical equipment across facilities.",
    badge: "Shock-Absorbing Fleet",
  },
  {
    id: "dot-hazmat",
    title: "DOT HAZMAT Transportation",
    description: "We provide secure, compliant transportation for chemotherapy medications and DOT-regulated hazardous materials, using trained couriers and proper handling procedures to protect patients, staff, and the community.",
    badge: "DOT-Regulated Handling",
  },
  {
    id: "medical-transport",
    title: "Medical Transportation",
    description: "Inter-facility direct shuttle logistics between hospital hubs, satellite clinics, and reference laboratories throughout the DMV.",
    badge: "Direct Door-to-Door",
  },
  {
    id: "senior-concierge",
    title: "Senior Concierge",
    description: "We provide personalized pharmacy delivery services ensuring medication with hands-on coordination, clear communication, and compassionate customer care, medical appointments, and supported in their assistance to help ensure personalized care.",
    badge: "Compassionate Care",
  },
];

export const Services = () => (
  <section id="services" data-testid="services-section" className="relative bg-white py-24 lg:py-32 scroll-mt-20">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Reveal>
              <Overline>Services — Chapter 01</Overline>
              <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
                Courier disciplines,<br />engineered for healthcare.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600 max-w-md">
                Customize the delivery of samples, medicine, and equipment through adaptable
                technology and a knowledgeable, certified team.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 relative rounded-[1.75rem] overflow-hidden aspect-[4/3] shadow-xl shadow-blue-900/10 border border-slate-100">
                <img src="/assets/stat-specimen-van.png" alt="Stat specimen secured in a blue medical cooler inside an HLM Sprinter van"
                     data-testid="services-lab-image" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1D4F]/60 to-transparent" />
                <p className="absolute bottom-4 left-5 font-mono text-[11px] font-semibold tracking-[0.2em] text-white/90 uppercase">
                  Lab-grade precision · Every mile
                </p>
              </div>
              <a href="#quote" data-testid="services-learn-more"
                 className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-[#0047FF] hover:gap-3 transition-all duration-200">
                Learn more about our service models <ArrowUpRight className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-7">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.06}>
              <div data-testid={`service-row-${s.id}`}
                   className="group border-t border-slate-200 py-8 flex gap-6 sm:gap-10 items-start hover:bg-blue-50/50 transition-colors duration-300 px-3 -mx-3 rounded-xl">
                <span className="font-mono text-sm font-semibold text-[#0047FF]/70 pt-1.5 shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex-1">
                  <h3 className="font-display text-xl sm:text-2xl font-bold text-slate-900 tracking-tight group-hover:text-[#0047FF] transition-colors duration-300">
                    {s.title}
                  </h3>
                  <p className="mt-2.5 text-sm sm:text-base leading-relaxed text-slate-600 max-w-lg">{s.description}</p>
                  <span className="mt-4 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-blue-50 text-[#0047FF] border border-blue-100">
                    {s.badge}
                  </span>
                </div>
                {s.img && (
                  <img src={s.img} alt={`${s.title} — HLM Courier`} data-testid={`service-image-${s.id}`}
                       className="hidden md:block w-44 aspect-[4/3] object-cover rounded-2xl border border-slate-100 shadow-md shrink-0" />
                )}
                <ArrowUpRight className="h-5 w-5 mt-2 text-slate-300 group-hover:text-[#0047FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300 shrink-0" />
              </div>
            </Reveal>
          ))}
          <div className="border-t border-slate-200" />
        </div>
      </div>
    </div>
  </section>
);
